import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import triviaData from '../assets/bar_mitzva_trivia.json';
import { getParasha, StoredParasha } from '../utils/userSelections';
import BottomNav from '../components/BottomNav';
import UserHeader from '../components/UserHeader';

const CLOUDFRONT_BASE = 'https://d1q1chclchjxl4.cloudfront.net';

const LETTERS = ['א', 'ב', 'ג', 'ד'];
const QUESTIONS_PER_QUIZ = 10;

interface Question {
  id: string;
  question: string;
  options: string[];
  answer: { letter: string; index: number };
}

const TriviaQuizPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Optional override (e.g. "next challenge" from the result page) — the user's
  // stored parasha selection stays untouched.
  const overrideParasha = (location.state as { parasha?: StoredParasha } | null)?.parasha ?? null;
  // Stable ref — getParasha() calls JSON.parse every render, producing a new object
  // reference each time. Storing it in a ref prevents useMemo from re-shuffling
  // questions on every state update.
  const parashaRef = useRef(overrideParasha ?? getParasha());
  const parasha = parashaRef.current;

  const questions = useMemo<Question[]>(() => {
    if (!parasha) return [];
    const match = triviaData.parshiyot.find((p) => p.parsha === parasha.hebrew);
    if (!match) return [];
    const shuffled = [...match.questions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, QUESTIONS_PER_QUIZ) as Question[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    Array(QUESTIONS_PER_QUIZ).fill(null),
  );
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const parashaImageUrl = parasha?.english
    ? `${CLOUDFRONT_BASE}/images/${parasha.english.toLowerCase()}.webp`
    : null;

  if (!parasha) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 bg-[#f9fafb] p-6">
        <p className="text-center text-lg font-bold text-gray-700">
          {t('trivia_quiz_no_parasha', 'לא נבחרה פרשה')}
        </p>
        <button
          onClick={() => navigate('/user-portal')}
          className="rounded-2xl bg-[#3B7BF4] px-8 py-1 font-bold text-white"
        >
          {t('trivia_quiz_back_home', 'חזרה הביתה')}
        </button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 bg-[#f9fafb] p-6">
        <p className="text-center text-lg font-bold text-gray-700">
          {t('trivia_quiz_no_questions', 'אין שאלות לפרשה זו')}
        </p>
        <button
          onClick={() => navigate('/user-portal')}
          className="rounded-2xl bg-[#3B7BF4] px-8 py-1 font-bold text-white"
        >
          {t('trivia_quiz_back_home', 'חזרה הביתה')}
        </button>
      </div>
    );
  }

  const current = questions[currentIndex];
  const correctIndex = current.answer.index;
  const selected = answers[currentIndex];
  const answered = selected !== null;

  const score = answers.reduce<number>((sum, ans, i) => {
    if (ans === null || !questions[i]) return sum;
    return sum + (ans === questions[i].answer.index ? 1 : 0);
  }, 0);

  const handleSelect = (idx: number) => {
    if (answered) return;
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIndex] = idx;
      return next;
    });
  };

  const handleNext = () => {
    if (!answered) return;
    if (currentIndex + 1 >= questions.length) {
      navigate('/trivia-result', {
        state: {
          score,
          total: questions.length,
          parasha: parasha!.hebrew,
          parashaEnglish: parasha!.english,
        },
      });
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex === 0) {
      navigate('/user-portal');
    } else {
      setCurrentIndex((i) => i - 1);
    }
  };

  const optionStyle = (idx: number): string => {
    const base =
      'flex cursor-pointer flex-row-reverse items-center gap-3 rounded-2xl border px-4 py-1 transition-all';
    if (!answered) {
      return selected === idx
        ? `${base} border-[#3B7BF4] bg-[#EEF3FE]`
        : `${base} border-gray-200 bg-white`;
    }
    if (idx === correctIndex) return `${base} border-green-400 bg-green-50`;
    if (idx === selected) return `${base} border-red-300 bg-red-50`;
    return `${base} border-gray-200 bg-white opacity-60`;
  };

  const letterBadgeStyle = (idx: number): string => {
    const base = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold';
    if (!answered) {
      return selected === idx
        ? `${base} bg-[#3B7BF4] text-white`
        : `${base} bg-gray-100 text-gray-600`;
    }
    if (idx === correctIndex) return `${base} bg-green-500 text-white`;
    if (idx === selected) return `${base} bg-red-400 text-white`;
    return `${base} bg-gray-100 text-gray-400`;
  };

  const radioStyle = (idx: number): string => {
    const base = 'h-5 w-5 shrink-0 rounded-full border-2 flex items-center justify-center';
    if (!answered) {
      return selected === idx
        ? `${base} border-[#3B7BF4] bg-[#3B7BF4]`
        : `${base} border-gray-300 bg-white`;
    }
    if (idx === correctIndex) return `${base} border-green-500 bg-green-500`;
    if (idx === selected) return `${base} border-red-400 bg-red-400`;
    return `${base} border-gray-300 bg-white`;
  };

  const progressPct = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[#f9fafb]">
      {/* Header */}
      <UserHeader />

      <div className="flex flex-1 flex-col overflow-hidden px-4 pb-6 pt-2">
        {/* Outer card wrapping image + questions */}
        <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
          {/* Parasha hero image — 70% width, centered */}
          <div className="flex justify-center ">
            <div
              className="relative w-full overflow-hidden bg-gradient-to-br from-[#1a3a6b] to-[#3B7BF4]"
              style={{ minHeight: imageLoaded && !imageError ? 0 : 110 }}
            >
              {parashaImageUrl && !imageError && (
                <img
                  src={parashaImageUrl}
                  alt={parasha.hebrew}
                  className="block h-36 w-full object-cover transition-opacity duration-300"
                  style={{ opacity: imageLoaded ? 1 : 0 }}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                />
              )}
              {(!imageLoaded || imageError) && (
                <div className="absolute inset-0 flex min-h-28 flex-col items-center justify-center gap-2 px-4 py-6">
                  <div
                    className="text-center text-3xl font-black leading-tight text-white"
                    style={{ textShadow: '0 2px 16px rgba(0,0,0,0.4)', direction: 'rtl' }}
                  >
                    {parasha.hebrew}
                  </div>
                </div>
              )}
              {imageLoaded && !imageError && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/20 to-transparent" />
              )}
            </div>
          </div>

          {/* Progress + questions */}
          <div className="flex flex-1 flex-col justify-between px-4 pb-4 pt-2">
            <div>
              <p className="mb-2 text-center text-sm text-gray-500">
                {t('trivia_quiz_question_of', {
                  current: currentIndex + 1,
                  total: questions.length,
                  defaultValue: `שאלה ${currentIndex + 1} מתוך ${questions.length}`,
                })}
              </p>
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
                style={{ direction: 'ltr' }}
              >
                <div
                  className="h-full rounded-full bg-[#3B7BF4] transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              {/* Question */}
              <p className="mt-2 text-center text-lg font-bold leading-snug text-[#1A1A2E]">
                {current.question}
              </p>

              {/* Options — key on currentIndex forces full remount between questions */}
              <div key={currentIndex} className="mt-5 flex flex-col gap-3">
                {current.options.map((option, idx) => (
                  <div key={idx} className={optionStyle(idx)} onClick={() => handleSelect(idx)}>
                    <div className={letterBadgeStyle(idx)}>{LETTERS[idx]}</div>
                    <span className="flex-1 text-right text-sm font-medium text-[#1A1A2E]">
                      {option}
                    </span>
                    <div className={radioStyle(idx)}>
                      {(selected === idx || (answered && idx === correctIndex)) && (
                        <div className="h-2 w-2 rounded-full bg-white" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div
              className="justify-center mt-6 flex items-center gap-3"
              style={{ direction: 'ltr' }}
            >
              <button
                onClick={handleBack}
                className=" py-[2px] px-[7px] flex p-1 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 bg-white text-gray-600 transition hover:border-gray-400"
                aria-label={t('back', 'חזור') as string}
              >
                ←
              </button>
              <button
                onClick={handleNext}
                disabled={!answered}
                className=" rounded-2xl bg-[#3B7BF4] py-1 font-bold text-white transition disabled:opacity-40"
              >
                {currentIndex + 1 === questions.length
                  ? t('trivia_quiz_finish', 'סיים')
                  : t('trivia_quiz_next', 'שאלה הבאה')}
              </button>
            </div>
          </div>
          {/* end progress + questions */}
        </div>
        {/* end outer card */}
      </div>

      <BottomNav />
    </div>
  );
};

export default TriviaQuizPage;
